import { Injectable } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { guid } from '@datorama/akita';
import { AkitaNgFormsManager } from '@datorama/akita-ng-forms-manager';
import {
	ComponentFormController,
	filterNil,
} from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { SelectItem } from 'primeng/api';
import { Observable } from 'rxjs';
import { finalize, map, shareReplay, take, tap } from 'rxjs/operators';

import {
	CoacheeMoodDialogState,
	CoacheeMoodDialogStore,
} from 'dialog/coachee-mood-dialog/coachee-mood-dialog.store';
import { UserMoodFrequency } from 'user/model/user-mood-frequency';
import { UserMoodApplicationPort } from 'user/port/mood/user-mood-application.port';
import { UserApplicationPort } from 'user/port/user-application.port';

@Injectable()
@UntilDestroy()
export class CoacheeMoodDialogController extends ComponentFormController<
	CoacheeMoodDialogState,
	any
> {
	public frequencyOptions$: Observable<SelectItem[]>;

	constructor(
		fb: FormBuilder,
		store: CoacheeMoodDialogStore,
		formsManager: AkitaNgFormsManager,
		private _translateService: TranslateService,
		private _userApplicationPort: UserApplicationPort,
		private _userMoodApplicationPort: UserMoodApplicationPort
	) {
		super(
			store,
			formsManager,
			`mood-rating-${guid()}`,
			fb.group({
				frequency: [UserMoodFrequency.DAY, Validators.required],
				mood: [null, Validators.required],
				feelings: [''],
			}),
			{ debounceTime: 250 }
		);

		this._userMoodApplicationPort
			.markMoodAsked()
			.pipe(untilDestroyed(this))
			.subscribe();

		this._initObservables();
		this._initListeners();
	}

	public saveMood(): Observable<string | Error> {
		this.store.update({ saving: true });

		const formValue: {
			frequency: UserMoodFrequency;
			feelings: string;
			mood: number;
		} = this.form.value;

		return this._userMoodApplicationPort
			.saveUserMood({
				frequency: formValue.frequency,
				feelings: formValue.feelings,
				mood: formValue.mood,
			})
			.pipe(
				finalize(() => this.store.update({ saving: false })),
				take(1)
			);
	}

	private _initListeners() {
		this._userApplicationPort
			.observeSessionUser()
			.pipe(
				filterNil(),
				take(1),
				tap((user) =>
					this.form.patchValue({
						frequency: user.metadata.moodFrequency || UserMoodFrequency.DAY,
					})
				),
				untilDestroyed(this)
			)
			.subscribe();
	}

	private _initObservables() {
		this.frequencyOptions$ = this._translateService
			.stream('presentation.app.common.dialog.coacheeMood.frequency.options')
			.pipe(
				map((frequencies) =>
					Object.keys(UserMoodFrequency).map(
						(frequency) =>
							({
								label: frequencies[frequency],
								value: frequency,
							} as SelectItem)
					)
				),
				shareReplay({ bufferSize: 1, refCount: true })
			);
	}
}
